import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, Optional, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { debounceTime, filter, map, merge, Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IResponse } from '@app/lib/models/response.model';
import { ICaseItem, ICaseSearchRequestBody, ISearchRequestParams } from '@app/dashboard/models/case-assignment.model';
import { DocumentReceptionService } from '@app/dashboard/services/document-reception.service';

@Component({
  selector: 'app-case-number-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule
  ],
  templateUrl: './case-number-search.component.html',
  styleUrls: ['./case-number-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CaseNumberSearchComponent),
      multi: true
    }
  ]
})
export class CaseNumberSearchComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string = 'Case Number';
  @Input() required: boolean = true;
  @Input() customSearchFn?: (query: string) => Observable<IResponse<ICaseItem[]>>;

  @Input() set externalCaseList(items: ICaseItem[]) {
    if (items && items.length > 0) {
      this.caseList = items;
      this.openAutocompletePanel();
    } else {
      this.clearCaseList();
    }
  }
  @Input() set externalLoader(loading: boolean) {
    this.caseListLoader = loading;
  }

  @Output() caseNumberChange = new EventEmitter<string>();
  @Output() middleSearchChange = new EventEmitter<string>();
  @Output() caseSelected = new EventEmitter<ICaseItem>();

  @ViewChild('middleInput') middleInputEl?: ElementRef<HTMLInputElement>;
  @ViewChild('autocompleteTrigger', { read: MatAutocompleteTrigger }) autocompleteTrigger?: MatAutocompleteTrigger;

  public caseList: ICaseItem[] = [];
  public caseListLoader: boolean = false;

  private cancelSearch$ = new Subject<void>();
  private activeSearchSub?: Subscription;
  private activePageSub?: Subscription;
  private lastSelectedCaseNumber: string | null = null;

  public stopBackendCalling(): void {
    this.cancelSearch$.next();
    if (this.activeSearchSub) {
      this.activeSearchSub.unsubscribe();
      this.activeSearchSub = undefined;
    }
    if (this.activePageSub) {
      this.activePageSub.unsubscribe();
      this.activePageSub = undefined;
    }
    this.caseListLoader = false;
    this.pageLoading = false;
  }

  public clearCaseList(): void {
    this.caseList = [];
    this.caseListLoader = false;
    this.pageLoading = false;
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.closePanel();
    }
    this.cdr.markForCheck();
  }

  public openAutocompletePanel(): void {
    const searchQuery = this.getCombinedCaseNumber();
    if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) {
      return;
    }
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    setTimeout(() => {
      if (this.caseList && this.caseList.length > 0) {
        if (this.autocompleteTrigger) {
          this.autocompleteTrigger.openPanel();
        }
        this.cdr.detectChanges();
      }
    }, 50);
  }

  public currentPage: number = 1;
  public pageSize: number = 25;
  public hasMoreRecords: boolean = true;
  public pageLoading: boolean = false;
  private currentSearchQuery: string = '';
  private scrollListenerAttached: boolean = false;

  public currentYearNum: number = new Date().getFullYear();
  public currentYear: string = this.currentYearNum.toString();
  public yearOptions: number[] = Array.from({ length: this.currentYearNum - 2000 + 1 }, (_, i) => this.currentYearNum - i);
  public showManualYearInput: boolean = false;

  private ensureYearOption(yr: number): void {
    if (!isNaN(yr) && yr > 0 && !this.yearOptions.includes(yr)) {
      this.yearOptions.push(yr);
      this.yearOptions.sort((a: number, b: number) => b - a);
    }
  }

  public searchFormGrp: FormGroup = new FormGroup({
    casePrefix: new FormControl(''),
    caseMiddle: new FormControl(''),
    caseSuffixYear: new FormControl(new Date().getFullYear().toString()),
    manualYear: new FormControl('')
  });

  private onChange: (val: string) => void = () => { };
  private onTouched: () => void = () => { };
  public isDisabled: boolean = false;
  prefixOptions: string[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    @Optional() private docReceptionService?: DocumentReceptionService
  ) {
    merge(
      this.searchFormGrp.controls['caseMiddle'].valueChanges,
      this.searchFormGrp.controls['casePrefix'].valueChanges,
      this.searchFormGrp.controls['caseSuffixYear'].valueChanges,
      this.searchFormGrp.controls['manualYear'].valueChanges
    )
      .pipe(
        takeUntilDestroyed(),
        map(() => {
          let suffixVal = this.searchFormGrp.controls['caseSuffixYear'].value;
          if (!suffixVal) {
            this.searchFormGrp.controls['caseSuffixYear'].setValue(this.currentYear, { emitEvent: false });
          }
          let middleVal = this.searchFormGrp.controls['caseMiddle'].value;
          if (typeof middleVal === 'string') {
            const sanitized = middleVal.replace(/[^a-zA-Z0-9\/]/g, '');
            if (sanitized !== middleVal) {
              this.searchFormGrp.controls['caseMiddle'].setValue(sanitized, { emitEvent: false });
              middleVal = sanitized;
            }
          }
          if (typeof middleVal === 'string' && middleVal.trim().length > 0) {
            if (middleVal.includes('/')) {
              const parts = middleVal.split('/').map((p: string) => p.trim()).filter((p: string) => p.length > 0);
              if (parts.length >= 2) {
                const firstPart = parts[0];
                const lastPart = parts[parts.length - 1];
                const hasPrefixMatch = this.prefixOptions.some(opt => opt.toUpperCase() === firstPart.toUpperCase());
                const yr = parseInt(lastPart, 10);
                const hasYearMatch = !isNaN(yr) && lastPart.length === 4 && yr >= 1900 && yr <= 2100;
                if (hasPrefixMatch && hasYearMatch) {
                  this.setCaseNumberValue(middleVal, false);
                  middleVal = this.searchFormGrp.controls['caseMiddle'].value;
                }
              }
            }
          }
          this.updateCombinedCaseNumber();
          const searchQuery = this.getCombinedCaseNumber();
          return { middleVal, searchQuery };
        }),
        filter(({ middleVal, searchQuery }) => {
          const middleStr = typeof middleVal === 'string' ? middleVal.trim() : (middleVal?.caseNumber || middleVal?.courtCaseNumber || '');
          if (middleStr.length < 2) {
            this.caseListLoader = false;
            this.caseList = [];
            this.cdr.markForCheck();
            return false;
          }
          if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) {
            this.caseListLoader = false;
            this.caseList = [];
            this.cdr.markForCheck();
            return false;
          }
          this.caseListLoader = true;
          this.currentPage = 1;
          this.hasMoreRecords = true;
          this.currentSearchQuery = searchQuery;
          return true;
        }),
        debounceTime(400),
        switchMap(({ searchQuery }) => {
          if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) {
            this.caseListLoader = false;
            this.caseList = [];
            return of({ data: [] } as unknown as IResponse<ICaseItem[]>);
          }
          this.middleSearchChange.emit(searchQuery);
          let searchObs$: Observable<IResponse<ICaseItem[]>>;
          if (this.customSearchFn) {
            searchObs$ = this.customSearchFn(searchQuery);
          } else if (this.docReceptionService) {
            const body = this.getSearchPayload() as unknown as ICaseSearchRequestBody;
            const params: ISearchRequestParams = { page: 1, start: 0, limit: this.pageSize, property: 'id', direction: 'ASC' };
            searchObs$ = this.docReceptionService.searchCaseNumberBasedprefix(body, params);
          } else {
            searchObs$ = of({ data: [] } as unknown as IResponse<ICaseItem[]>);
          }
          return searchObs$.pipe(takeUntil(this.cancelSearch$));
        })
      )
      .subscribe({
        next: (res: IResponse<ICaseItem[]>) => {
          const searchQuery = this.getCombinedCaseNumber();
          if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) {
            this.clearCaseList();
            return;
          }
          const newItems = res.data || [];
          this.caseList = newItems;
          this.hasMoreRecords = newItems.length >= this.pageSize;
          this.caseListLoader = false;
          if (newItems.length > 0) {
            this.openAutocompletePanel();
          } else {
            if (this.autocompleteTrigger) {
              this.autocompleteTrigger.closePanel();
            }
          }
        },
        error: () => {
          this.caseListLoader = false;
        }
      });
  }

  public onPanelOpened(): void {
    setTimeout(() => {
      const panel = document.querySelector('.mat-mdc-autocomplete-panel') || document.querySelector('.mat-autocomplete-panel');
      if (panel && !this.scrollListenerAttached) {
        this.scrollListenerAttached = true;
        panel.addEventListener('scroll', (e: Event) => this.onPanelScroll(e));
      }
    }, 100);
  }

  public onPanelScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;
    const threshold = 25;
    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (atBottom && !this.caseListLoader && !this.pageLoading && this.hasMoreRecords) {
      this.loadNextPage();
    }
  }

  public loadNextPage(): void {
    const searchQuery = this.getCombinedCaseNumber();
    if (this.pageLoading || !this.hasMoreRecords || (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber)) return;
    this.pageLoading = true;
    const nextPage = this.currentPage + 1;
    const start = (nextPage - 1) * this.pageSize;

    if (this.docReceptionService) {
      const body = this.getSearchPayload() as unknown as ICaseSearchRequestBody;
      const params: ISearchRequestParams = { page: nextPage, start: start, limit: this.pageSize, property: 'id', direction: 'ASC' };
      this.activePageSub = this.docReceptionService.searchCaseNumberBasedprefix(body, params)
        .pipe(takeUntil(this.cancelSearch$))
        .subscribe({
          next: (res: IResponse<ICaseItem[]>) => {
            if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) {
              this.pageLoading = false;
              return;
            }
            const newItems = res.data || [];
            this.caseList = [...this.caseList, ...newItems];
            this.currentPage = nextPage;
            this.hasMoreRecords = newItems.length >= this.pageSize;
            this.pageLoading = false;
          },
          error: () => {
            this.pageLoading = false;
          }
        });
    } else {
      this.pageLoading = false;
    }
  }

  ngOnInit(): void {
    this.getCourtNameData();
    this.initPrefixControl();
    this.updateCombinedCaseNumber();
  }

  ngOnDestroy(): void {
    this.stopBackendCalling();
  }

  // ControlValueAccessor methods
  writeValue(obj: any): void {
    if (typeof obj === 'string') {
      this.setCaseNumberValue(obj, true);
    } else if (obj && typeof obj === 'object') {
      const caseNo = obj.caseNumber || obj.courtCaseNumber || '';
      this.setCaseNumberValue(caseNo, true);
    } else {
      this.setCaseNumberValue('', true);
    }
    this.lastSelectedCaseNumber = this.getCombinedCaseNumber();
    this.stopBackendCalling();
    this.clearCaseList();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  getCourtNameData(): void {
    try {
      const userData = localStorage.getItem('userData') as string;
      const userData2 = userData ? JSON.parse(userData) : null;
      if (userData2.courtCode === "SC") {
        this.prefixOptions = ["SC", "SCN"]
      } else {
        this.prefixOptions = [userData2.courtCode]
      }

    } catch (err) {
      return
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.searchFormGrp.disable({ emitEvent: false });
    } else {
      this.searchFormGrp.enable({ emitEvent: false });
      this.initPrefixControl();
    }
  }

  public initPrefixControl(): void {
    const defaultPrefix = this.prefixOptions.length > 0 ? this.prefixOptions[0] : '';
    const prefixCtrl = this.searchFormGrp.get('casePrefix');
    if (prefixCtrl) {
      if (!prefixCtrl.value) {
        prefixCtrl.setValue(defaultPrefix, { emitEvent: false });
      }
      if (this.prefixOptions.length === 1) {
        prefixCtrl.disable({ emitEvent: false });
      } else if (!this.isDisabled) {
        prefixCtrl.enable({ emitEvent: false });
      }
    }
  }

  public onSuffixYearSelect(event: Event): void {
    const val = (event.target as HTMLSelectElement)?.value || '';
    if (val === 'CUSTOM_YEAR') {
      this.showManualYearInput = true;
      this.searchFormGrp.patchValue({ manualYear: '' }, { emitEvent: false });
    } else {
      const yearToSet = val || this.currentYear;
      this.showManualYearInput = false;
      this.searchFormGrp.patchValue({ caseSuffixYear: yearToSet, manualYear: '' }, { emitEvent: false });
      this.lastSelectedCaseNumber = null;
      this.updateCombinedCaseNumber();
    }
  }

  public confirmCustomYear(): void {
    const manualYr = (this.searchFormGrp.get('manualYear')?.value || '').toString().trim();
    const yr = parseInt(manualYr, 10);
    if (!isNaN(yr) && yr > 1900 && yr < 2100) {
      this.ensureYearOption(yr);
      this.showManualYearInput = false;
      this.searchFormGrp.patchValue({
        caseSuffixYear: yr.toString(),
        manualYear: ''
      });
    } else if (!manualYr) {
      this.cancelCustomYear();
    }
    this.lastSelectedCaseNumber = null;
    this.updateCombinedCaseNumber();
  }

  public cancelCustomYear(): void {
    this.showManualYearInput = false;
    this.searchFormGrp.patchValue({
      caseSuffixYear: this.currentYear,
      manualYear: ''
    });
    this.updateCombinedCaseNumber();
  }

  public onMiddleInput(event?: Event): void {
    this.lastSelectedCaseNumber = null;
  }

  public onMiddleKeyPress(event: KeyboardEvent): void {
    const char = event.key;
    if (!char || char.length > 1) return;
    const isAllowed = /[a-zA-Z0-9\/]/.test(char);
    if (!isAllowed) {
      event.preventDefault();
    }
  }

  public onMiddlePaste(event: ClipboardEvent): void {
    const rawPasted = event.clipboardData?.getData('text') || '';
    if (rawPasted) {
      const sanitized = rawPasted.replace(/[^a-zA-Z0-9\/]/g, '');
      if (sanitized) {
        event.preventDefault();
        this.lastSelectedCaseNumber = null;
        this.setCaseNumberValue(sanitized, false);
        this.triggerImmediateSearch();
      }
    }
  }

  public onMiddleFocus(): void {
    const searchQuery = this.getCombinedCaseNumber();
    if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) return;
    if (this.autocompleteTrigger && !this.autocompleteTrigger.panelOpen) {
      if (this.caseList && this.caseList.length > 0) {
        this.openAutocompletePanel();
      } else {
        const middleVal = (this.searchFormGrp.get('caseMiddle')?.value || '').toString().trim();
        if (middleVal.length >= 2) {
          this.triggerImmediateSearch();
        }
      }
    }
  }

  public onMiddleClick(): void {
    const searchQuery = this.getCombinedCaseNumber();
    if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) return;
    if (this.autocompleteTrigger && !this.autocompleteTrigger.panelOpen) {
      if (this.caseList && this.caseList.length > 0) {
        this.openAutocompletePanel();
      } else {
        const middleVal = (this.searchFormGrp.get('caseMiddle')?.value || '').toString().trim();
        if (middleVal.length >= 2) {
          this.triggerImmediateSearch();
        }
      }
    }
  }

  public triggerImmediateSearch(): void {
    const rawMiddle = this.searchFormGrp.get('caseMiddle')?.value;
    const middleStr = typeof rawMiddle === 'string' ? rawMiddle.trim() : (rawMiddle?.caseNumber || rawMiddle?.courtCaseNumber || '');
    const searchQuery = this.getCombinedCaseNumber();

    if (middleStr.length < 2 || (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber)) {
      this.clearCaseList();
      return;
    }

    this.updateCombinedCaseNumber();
    this.caseListLoader = true;
    this.currentPage = 1;
    this.hasMoreRecords = true;
    this.currentSearchQuery = searchQuery;
    this.middleSearchChange.emit(searchQuery);

    this.stopBackendCalling();

    let searchObs$: Observable<IResponse<ICaseItem[]>> | null = null;
    if (this.customSearchFn) {
      searchObs$ = this.customSearchFn(searchQuery);
    } else if (this.docReceptionService) {
      const body = this.getSearchPayload() as unknown as ICaseSearchRequestBody;
      const params: ISearchRequestParams = { page: 1, start: 0, limit: this.pageSize, property: 'id', direction: 'ASC' };
      searchObs$ = this.docReceptionService.searchCaseNumberBasedprefix(body, params);
    }

    if (searchObs$) {
      this.activeSearchSub = searchObs$.pipe(takeUntil(this.cancelSearch$)).subscribe({
        next: (res: IResponse<ICaseItem[]>) => {
          if (this.lastSelectedCaseNumber && searchQuery === this.lastSelectedCaseNumber) {
            this.clearCaseList();
            return;
          }
          const newItems = res.data || [];
          this.caseList = newItems;
          this.hasMoreRecords = newItems.length >= this.pageSize;
          this.caseListLoader = false;
          if (newItems.length > 0) {
            this.openAutocompletePanel();
          } else {
            if (this.autocompleteTrigger) {
              this.autocompleteTrigger.closePanel();
            }
          }
        },
        error: () => {
          this.caseListLoader = false;
        }
      });
    }
  }

  public getSearchPayload(): { prefix: string; input: string; suffix: string } {
    const rawVal = this.searchFormGrp.getRawValue();
    let prefix = (rawVal.casePrefix || '').toString().trim();
    if (prefix && !prefix.endsWith('/')) {
      prefix += '/';
    }

    let input = '';
    const middleVal = rawVal.caseMiddle;
    if (typeof middleVal === 'object' && middleVal !== null) {
      const full = middleVal.caseNumber || middleVal.courtCaseNumber || '';
      if (full.includes('/')) {
        const parts = full.split('/').map((p: string) => p.trim()).filter((p: string) => p.length > 0);
        if (parts.length >= 3) {
          input = parts.slice(1, parts.length - 1).join('/');
        } else {
          input = full;
        }
      } else {
        input = full;
      }
    } else {
      input = (middleVal || '').toString().trim();
    }
    input = input.replace(/^\/+|\/+$/g, '');

    const manualYr = (rawVal.manualYear || '').toString().trim();
    const suffix = (this.showManualYearInput && manualYr)
      ? manualYr
      : (rawVal.caseSuffixYear === 'CUSTOM_YEAR' ? '' : (rawVal.caseSuffixYear || '').toString().trim());

    return { prefix, input, suffix };
  }

  public getCombinedCaseNumber(): string {
    const rawVal = this.searchFormGrp.getRawValue();
    const prefix = (rawVal.casePrefix || '').toString().trim().replace(/^\/+|\/+$/g, '');
    const middleVal = rawVal.caseMiddle;
    const suffixVal = rawVal.caseSuffixYear;
    const manualYr = (rawVal.manualYear || '').toString().trim();

    let middleStr = '';
    if (typeof middleVal === 'object' && middleVal !== null) {
      const full = middleVal.caseNumber || middleVal.courtCaseNumber || '';
      if (full.includes('/')) {
        const parts = full.split('/').map((p: string) => p.trim()).filter((p: string) => p.length > 0);
        if (parts.length >= 3) {
          middleStr = parts.slice(1, parts.length - 1).join('/');
        } else {
          middleStr = full;
        }
      } else {
        middleStr = full;
      }
    } else {
      middleStr = (middleVal || '').toString().trim();
    }
    middleStr = middleStr.replace(/^\/+|\/+$/g, '');

    const year = (this.showManualYearInput && manualYr) ? manualYr : (suffixVal === 'CUSTOM_YEAR' ? '' : (suffixVal || '').toString().trim());

    const parts: string[] = [];
    if (prefix) parts.push(prefix);
    if (middleStr) parts.push(middleStr);
    if (year) parts.push(year);

    const combined = parts.join('/').replace(/\/+/g, '/');
    return combined;
  }

  public updateCombinedCaseNumber(): void {
    const combined = this.getCombinedCaseNumber();
    this.onChange(combined);
    this.onTouched();
    this.caseNumberChange.emit(combined);
  }

  public setCaseNumberValue(fullCaseNo: string, isProgrammatic: boolean = false): void {
    const defaultPrefix = this.prefixOptions.length > 0 ? this.prefixOptions[0] : '';
    if (!fullCaseNo) {
      this.searchFormGrp.patchValue({
        casePrefix: defaultPrefix,
        caseMiddle: '',
        caseSuffixYear: this.currentYear,
        manualYear: ''
      }, { emitEvent: false });
      this.showManualYearInput = false;
      if (isProgrammatic) {
        this.stopBackendCalling();
        this.clearCaseList();
      }
      this.initPrefixControl();
      this.updateCombinedCaseNumber();
      this.cdr.markForCheck();
      return;
    }

    let searchStr = fullCaseNo;
    if (typeof fullCaseNo !== 'string') {
      searchStr = (fullCaseNo as any).caseNumber || (fullCaseNo as any).courtCaseNumber || '';
    }

    const parts = searchStr.split('/').map((p: string) => p.trim()).filter((p: string) => p.length > 0);

    if (parts.length === 0) {
      this.searchFormGrp.patchValue({
        casePrefix: defaultPrefix,
        caseMiddle: '',
        caseSuffixYear: this.currentYear,
        manualYear: ''
      }, { emitEvent: false });
      this.showManualYearInput = false;
      if (isProgrammatic) {
        this.stopBackendCalling();
        this.clearCaseList();
      }
      this.initPrefixControl();
      this.updateCombinedCaseNumber();
      this.cdr.markForCheck();
      return;
    }

    // 1. Check Prefix Match (only match against available prefixOptions; do NOT add new prefix to dropdown)
    const firstPart = parts[0];
    const matchedPrefix = this.prefixOptions.find(opt => opt.toUpperCase() === firstPart.toUpperCase());
    const hasPrefixMatch = !!matchedPrefix;

    // 2. Check Year Match (only if parts.length >= 2, check if last part is a 4-digit year)
    let hasYearMatch = false;
    let suffixYear = this.currentYear;

    if (parts.length >= 2) {
      const lastPart = parts[parts.length - 1];
      const yr = parseInt(lastPart, 10);
      if (!isNaN(yr) && lastPart.length === 4 && yr >= 1900 && yr <= 2100) {
        hasYearMatch = true;
        this.ensureYearOption(yr);
        suffixYear = yr.toString();
      }
    }

    let selectedPrefix = defaultPrefix;
    let middle = '';

    if (hasPrefixMatch && hasYearMatch) {
      selectedPrefix = matchedPrefix;
      middle = parts.slice(1, parts.length - 1).join('/');
    } else if (hasPrefixMatch && !hasYearMatch) {
      selectedPrefix = matchedPrefix;
      middle = parts.slice(1).join('/');
    } else if (!hasPrefixMatch && hasYearMatch) {
      const currentPrefixCtrlVal = this.searchFormGrp.get('casePrefix')?.value;
      if (currentPrefixCtrlVal && this.prefixOptions.includes(currentPrefixCtrlVal)) {
        selectedPrefix = currentPrefixCtrlVal;
      } else {
        selectedPrefix = defaultPrefix;
      }
      middle = parts.slice(0, parts.length - 1).join('/');
    } else {
      const currentPrefixCtrlVal = this.searchFormGrp.get('casePrefix')?.value;
      if (currentPrefixCtrlVal && this.prefixOptions.includes(currentPrefixCtrlVal)) {
        selectedPrefix = currentPrefixCtrlVal;
      } else {
        selectedPrefix = defaultPrefix;
      }
      middle = searchStr;
    }

    this.showManualYearInput = false;
    this.searchFormGrp.patchValue({
      casePrefix: selectedPrefix,
      caseMiddle: middle,
      caseSuffixYear: suffixYear,
      manualYear: ''
    }, { emitEvent: false });

    if (middle.length < 2 && isProgrammatic) {
      this.clearCaseList();
    }

    this.initPrefixControl();
    this.cdr.markForCheck();
  }

  public onCaseOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as ICaseItem;
    if (item) {
      this.stopBackendCalling();
      const caseNo = item.caseNumber || (item as any).courtCaseNumber || '';
      if (caseNo) {
        this.setCaseNumberValue(caseNo, true);
      }
      this.lastSelectedCaseNumber = this.getCombinedCaseNumber();
      this.caseSelected.emit(item);
      this.clearCaseList();
      setTimeout(() => {
        if (this.middleInputEl?.nativeElement) {
          this.middleInputEl.nativeElement.blur();
        }
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 0);
    }
  }

  public caseDisplayFn(item: ICaseItem | string): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    const full = item.caseNumber || (item as any).courtCaseNumber || '';
    if (full.includes('/')) {
      const parts = full.split('/').map((p: string) => p.trim()).filter((p: string) => p.length > 0);
      const hasPrefixMatch = parts.length >= 1 && this.prefixOptions.some(opt => opt.toUpperCase() === parts[0].toUpperCase());
      const lastPart = parts[parts.length - 1];
      const yr = parseInt(lastPart, 10);
      const hasYearMatch = parts.length >= 2 && !isNaN(yr) && lastPart.length === 4 && yr >= 1900 && yr <= 2100;

      let startIdx = hasPrefixMatch ? 1 : 0;
      let endIdx = hasYearMatch ? parts.length - 1 : parts.length;
      if (startIdx < endIdx) {
        return parts.slice(startIdx, endIdx).join('/');
      }
      return full;
    }
    return full;
  }
}
